/**
 * Step arithmetic and formatting for target caloric density in the URC calculator.
 *
 * The `step*` helpers return a position on the {@link CALORIE_DENSITY_STEP} scale — a **step
 * count**, not a density; {@link fromSteps} converts back. Counting in integers keeps values
 * exact: adding 0.1 repeatedly drifts (`0.1 * 7 === 0.7000000000000001`).
 *
 * Pure by design — callers pass the resolved conversion rate.
 */

import { KCal, Units } from 'src/app/app.enums';

/** The unit a target caloric density is expressed in. */
export type CalorieUom = Units.Milliliters | Units.Ounce;

/** Increment between selectable densities, per unit. */
export const CALORIE_DENSITY_STEP: Record<CalorieUom, number> = {
	[Units.Ounce]: 1,
	[Units.Milliliters]: 0.1,
};

/**
 * Decimals shown, per unit — not derivable from the step, which only bounds *selectable* values:
 * a calculated 0.756 kcal/mL shows as 0.76, not 0.8.
 */
const CALORIE_DENSITY_DECIMALS: Record<CalorieUom, number> = {
	[Units.Ounce]: 0,
	[Units.Milliliters]: 2,
};

const CALORIE_DENSITY_LABEL: Record<CalorieUom, KCal> = {
	[Units.Ounce]: KCal.Ounce,
	[Units.Milliliters]: KCal.Milliliter,
};

/**
 * Converting a bound between units (`fromOz`) can leave it a hair under a whole step: with
 * mlPerOz 29.5735, `(30 / mlPerOz) * mlPerOz` is 29.999999999999996. A bare Math.floor reads
 * that as 29, offering a 29 kcal/oz max where 30 was configured. Which values land short
 * depends on the tenant's mlPerOz, so they can't just be listed here. The 0.1 scale has the
 * same problem: 0.7 / 0.1 === 6.999999999999999.
 *
 * 1e-9 is far larger than that error (~1e-15) and far smaller than the smallest difference the
 * UI can show (0.01 kcal/mL), so the exact value doesn't matter.
 */
const EPS = 1e-9;

/** Highest step at or below `value`, as a **count**: `stepFloor(0.7, 0.1)` is `7`. */
export const stepFloor = (value: number, step: number): number =>
	Math.floor(value / step + EPS);

/** Lowest step at or above `value`, as a **count**: `stepCeil(0.61, 0.1)` is `7`. */
export const stepCeil = (value: number, step: number): number =>
	Math.ceil(value / step - EPS);

/**
 * Nearest step to `value`, as a **count**: `stepRound(0.72, 0.1)` is `7`. No EPS needed —
 * Math.round already absorbs the drift.
 */
export const stepRound = (value: number, step: number): number =>
	Math.round(value / step);

/** Converts a step count back to a clean density in `uom` — the inverse of the `step*` helpers. */
const fromSteps = (steps: number, uom: CalorieUom): number =>
	Number(
		(steps * CALORIE_DENSITY_STEP[uom]).toFixed(CALORIE_DENSITY_DECIMALS[uom]),
	);

/** Converts a configured bound into the working unit; tenant min/max are stored in kcal/oz only. */
export const fromOz = (
	valueInOz: number,
	uom: CalorieUom,
	mlPerOz: number,
): number => (uom === Units.Ounce ? valueInOz : valueInOz / mlPerOz);

/** Converts kcal/mL into the working unit. */
export const toWorkingUom = (
	kcalPerMl: number,
	uom: CalorieUom,
	mlPerOz: number,
): number => (uom === Units.Milliliters ? kcalPerMl : kcalPerMl * mlPerOz);

/**
 * Every selectable density between the bounds, inclusive. Both are **step counts**,
 * not densities — pass the output of `stepCeil` / `stepFloor`.
 */
export const getDensityRange = (
	minSteps: number,
	maxSteps: number,
	uom: CalorieUom,
): number[] => {
	if (minSteps > maxSteps) {
		return [];
	}

	return Array.from({ length: maxSteps - minSteps + 1 }, (_, i) =>
		fromSteps(minSteps + i, uom),
	);
};

/**
 * The value alone at the unit's precision, trailing zeros kept (`0.70`, `24`). Fixed rather than
 * trimmed so options keep a stable width — `0.70` shouldn't render as `0.7` next to `0.75`.
 */
const formatDensityValue = (value: number, uom: CalorieUom): string =>
	value.toFixed(CALORIE_DENSITY_DECIMALS[uom]);

/** e.g. `"24 kcal/oz"`, `"0.70 kcal/mL"`. */
export const formatCaloricDensity = (value: number, uom: CalorieUom): string =>
	`${formatDensityValue(value, uom)} ${CALORIE_DENSITY_LABEL[uom]}`;
